import { computed, defineComponent, reactive, ref } from 'vue';
import { useStore } from 'vuex';
import { useSweetAlert } from '@/composables/sweetalert';
import { EmergencyAnswerAction, EmergencyAnswerGetter } from '@/store/emergencyAnswer/emergencyAnswer.module';
import { EmergencyAnswerSchema } from '@/store/emergencyAnswer/emergencyAnswer.validator';
import { EmergencyAnswer } from '@/store/emergencyAnswer/emergencyAnswer.model';
import BaseModalComponent from '@/components/baseModal/BaseModal.vue';
import Datepicker from '@vuepic/vue-datepicker';

export default defineComponent({
  name: 'EmergencyAnswerFormComponent',
  components: {
    BaseModalComponent,
    Datepicker,
  },
  props: {
    emergencyId: {
      type: Number,
    },
    emergencyAnswerId: {
      type: Number,
    },
  },
  setup: (props) => {
    const swal = useSweetAlert();
    const store = useStore();
    const isEditMode = computed(() => !!props.emergencyAnswerId);
    const title = computed(() => isEditMode.value ? 'แก้ไขเหตุการณ์' : 'เพิ่มเหตุการณ์');
    const emergencyAnswer = computed<EmergencyAnswer | undefined>(() => store.getters[EmergencyAnswerGetter.BY_ID](props.emergencyAnswerId));
    const emergencyAnswerForm = ref<InstanceType<typeof BaseModalComponent>>();

    // form
    interface Form {
      id?: number | null;
      description?: string;
      answerDate?: Date | null;
      emergencyId: number;
    }
    const formData = reactive<Form>({
      id: null,
      description: '',
      answerDate: null,
      emergencyId: props.emergencyId as number,
    });
    const validator = computed(() => EmergencyAnswerSchema.validate(formData, { abortEarly: false }));
    const errors = reactive({
      description: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'description')),
      answerDate: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'answerDate')),
    });
    const isValid = computed(() => !errors.description && !errors.answerDate);
    const isLoading = computed<boolean>(() => store.getters[EmergencyAnswerGetter.STATUS] === 'init' || store.getters[EmergencyAnswerGetter.STATUS] === 'loading');
    const submited = ref(false);

    // show modal
    const show = () => {
      new Promise<void>((resolve) => {
        formData.emergencyId = props.emergencyId as number;

        if (isEditMode.value) {
          formData.id = emergencyAnswer.value?.id;
          formData.description = emergencyAnswer.value?.description;
          formData.answerDate = emergencyAnswer.value?.answerDate;
        }
        resolve();
      })
      .then(() => emergencyAnswerForm.value?.show());
    };

    // hide modal
    const hide = () => {
      emergencyAnswerForm.value?.hide();
    };

    // clear submit status
    const unsubmit = () => {
      submited.value = false;
    };

    // clear form
    const clearForm = () => {
      new Promise<void>((resolve) => {
        formData.id = null;
        formData.description = '';
        formData.answerDate = null;
        formData.emergencyId = props.emergencyId as number;
        resolve();
      }).then(() => unsubmit());
    };

    // submit form
    const submitClick = () => {
      submited.value = true;

      if (isValid.value) {
        if (isEditMode.value) {
          store.dispatch(EmergencyAnswerAction.UPDATE, formData)
            .then(() => hide());
        } else {
          store.dispatch(EmergencyAnswerAction.CREATE, formData)
            .then(() => hide());
        }
      }
    };

    // delete item
    const deleteClick = () => {
      swal.confirm('ลบข้อมูล!', 'คุณต้องการลบข้อมูลใช่หรือไม่?', 'ใช่ ลบเดี๋ยวนี้!', '#dc3545')
        .then((result) => {
          if (result.isConfirmed) {
            store.dispatch(EmergencyAnswerAction.DELETE, formData)
              .then(() => hide());
          }
        });
    };

    return {
      title,
      formData,
      isEditMode,
      errors,
      submited,
      isLoading,
      emergencyAnswerForm,
      show,
      hide,
      submitClick,
      deleteClick,
      unsubmit,
      clearForm,
    };
  },
});
